import React, { useEffect, useMemo, useRef, useState } from "react";
import { View, StyleSheet, Animated, Easing } from "react-native";
import NtTouchableEffect from "./NtTouchableEffect";
import NtMaterialIcon from "./NtMaterialIcon";
import NtText from "./NtText";
import { useTheme } from "../context/ThemeContext";
import Logger from "../common/utils/Logger";

const WAVEFORM_BARS = 32;
const FALLBACK_PEAKS = new Array(WAVEFORM_BARS).fill(0.3);

/**
 * Reusable audio player with play/pause/stop and a SoundCloud-style waveform
 * (the bars show the audio's actual peaks, and fill left-to-right as it plays).
 *
 *   <NtAudioPlayer
 *     loadAudio={async () => {
 *       const r = await fetchRecordingAudio(domain, id);
 *       return r.ok ? r.data : null;   // expects a Blob
 *     }}
 *   />
 *
 * `loadAudio` must return a Blob (or null on failure). The Blob is used for
 * BOTH playback (via blob: URL) and waveform decoding (via decodeAudioData).
 */
function NtAudioPlayer({
  loadAudio,
  onPlayStarted,
  onPlayStopped,
  containerStyle,
}) {
  const { theme } = useTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);

  const audioRef = useRef();
  const blobUrlRef = useRef(null);
  const rafRef = useRef();

  const [peaks, setPeaks] = useState(FALLBACK_PEAKS);
  const [status, setStatus] = useState("idle"); // idle | loading | playing | paused
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);

  // Continuous pulse used by bars near the playhead while playing.
  const pulse = useRef(new Animated.Value(0)).current;
  const pulseLoop = useRef();

  /** Decode audio bytes → array of WAVEFORM_BARS peak amplitudes (0..1). */
  const computePeaks = async (blob) => {
    if (typeof window === "undefined") return null;
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return null;

    let ctx;
    try {
      const buffer = await blob.arrayBuffer();
      ctx = new Ctx();
      const audioBuffer = await ctx.decodeAudioData(buffer);
      const channel = audioBuffer.getChannelData(0); // mono — first channel is enough for shape
      const samplesPerBar = Math.floor(channel.length / WAVEFORM_BARS);
      const out = new Array(WAVEFORM_BARS);
      let max = 0;
      for (let i = 0; i < WAVEFORM_BARS; i++) {
        let peak = 0;
        const start = i * samplesPerBar;
        const end = start + samplesPerBar;
        for (let j = start; j < end; j++) {
          const v = Math.abs(channel[j]);
          if (v > peak) peak = v;
        }
        out[i] = peak;
        if (peak > max) max = peak;
      }
      // Normalize so the loudest bar = 1.0; floor at 0.08 so silence is still visible.
      const norm = max > 0 ? max : 1;
      for (let i = 0; i < WAVEFORM_BARS; i++) {
        out[i] = Math.max(0.08, out[i] / norm);
      }
      return out;
    } catch (e) {
      Logger("NtAudioPlayer: decode failed, falling back to placeholder bars", e);
      return null;
    } finally {
      if (ctx) ctx.close().catch(() => {});
    }
  };

  /** Loads + decodes the audio without playing. Idempotent. */
  const ensureLoaded = async () => {
    const el = audioRef.current;
    if (!el || blobUrlRef.current || !loadAudio) return;

    const blob = await loadAudio();
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    blobUrlRef.current = url;
    el.src = url;
    el.load();

    const p = await computePeaks(blob);
    if (p) setPeaks(p);
  };

  // Pre-load + decode on mount so the user sees the waveform shape and
  // duration without having to press play first.
  useEffect(() => {
    ensureLoaded();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePlay = async () => {
    const el = audioRef.current;
    if (!el) return;

    // In case mount-time prefetch hasn't finished yet.
    if (!blobUrlRef.current) {
      setStatus("loading");
      await ensureLoaded();
    }

    if (!blobUrlRef.current) {
      setStatus("idle");
      return;
    }

    try {
      await el.play();
    } catch (e) {
      Logger("NtAudioPlayer: play error", e);
      setStatus("idle");
    }
  };

  const handlePause = () => {
    audioRef.current?.pause();
    setStatus("paused");
  };

  const handleStop = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setProgress(0);
    setStatus("idle");
    onPlayStopped?.();
  };

  const handleTogglePlay = () => {
    if (status === "playing") handlePause();
    else handlePlay();
  };

  // Run the pulse loop + a 60fps RAF that reads currentTime, so the playhead
  // sweeps smoothly between bars instead of stepping with the slow `timeupdate`
  // event (browsers throttle it to ~4–5/sec).
  useEffect(() => {
    if (status !== "playing") {
      pulseLoop.current?.stop();
      Animated.timing(pulse, { toValue: 0, duration: 200, useNativeDriver: false }).start();
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      return;
    }
    pulseLoop.current = Animated.loop(
      Animated.sequence([
        Animated.timing(pulse, {
          toValue: 1,
          duration: 420,
          easing: Easing.inOut(Easing.quad),
          useNativeDriver: false,
        }),
        Animated.timing(pulse, {
          toValue: 0,
          duration: 420,
          easing: Easing.inOut(Easing.quad),
          useNativeDriver: false,
        }),
      ]),
    );
    pulseLoop.current.start();

    const tick = () => {
      const t = audioRef.current?.currentTime;
      if (typeof t === "number") setProgress(t);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      pulseLoop.current?.stop();
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [status, pulse]);

  // <audio> events.
  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;

    const onPlaying = () => {
      setStatus("playing");
      onPlayStarted?.();
    };
    const onPause = () => {
      if (el.ended) return;
      setStatus("paused");
    };
    const onTime = () => setProgress(el.currentTime || 0);
    const onLoaded = () => setDuration(el.duration || 0);
    const onEnded = () => {
      setProgress(0);
      setStatus("idle");
      onPlayStopped?.();
    };
    const onError = () => Logger("NtAudioPlayer: <audio> error", el.error);

    el.addEventListener("playing", onPlaying);
    el.addEventListener("pause", onPause);
    el.addEventListener("timeupdate", onTime);
    el.addEventListener("loadedmetadata", onLoaded);
    el.addEventListener("ended", onEnded);
    el.addEventListener("error", onError);
    return () => {
      el.removeEventListener("playing", onPlaying);
      el.removeEventListener("pause", onPause);
      el.removeEventListener("timeupdate", onTime);
      el.removeEventListener("loadedmetadata", onLoaded);
      el.removeEventListener("ended", onEnded);
      el.removeEventListener("error", onError);
    };
  }, [onPlayStarted, onPlayStopped]);

  // Cleanup blob URL on unmount.
  useEffect(() => {
    return () => {
      if (blobUrlRef.current) {
        try { URL.revokeObjectURL(blobUrlRef.current); } catch {}
      }
    };
  }, []);

  // Click on the waveform → seek to that position.
  const handleSeek = (event) => {
    const el = audioRef.current;
    if (!el || !duration) return;
    const target = event.currentTarget;
    if (!target?.getBoundingClientRect) return;
    const rect = target.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const fraction = Math.max(0, Math.min(1, x / rect.width));
    el.currentTime = fraction * duration;
    setProgress(el.currentTime);
  };

  const formatTime = (s) => {
    if (!Number.isFinite(s) || s < 0) return "0:00";
    const m = Math.floor(s / 60);
    const r = Math.floor(s % 60).toString().padStart(2, "0");
    return `${m}:${r}`;
  };

  const isPlaying = status === "playing";
  const isLoading = status === "loading";
  const playedFraction = duration > 0 ? progress / duration : 0;

  return (
    <View style={[styles.container, containerStyle]}>
      <NtTouchableEffect style={styles.primaryBtn} onPress={handleTogglePlay}>
        <NtMaterialIcon
          name={isLoading ? "loading" : isPlaying ? "pause" : "play"}
          color="#ffffff"
          size={14}
        />
      </NtTouchableEffect>

      <NtTouchableEffect
        style={[styles.secondaryBtn, status === "idle" && styles.btnDisabled]}
        onPress={handleStop}
        disablePress={status === "idle"}
      >
        <NtMaterialIcon name="stop" color={theme.textDim} size={12} />
      </NtTouchableEffect>

      <div onClick={handleSeek} style={waveDivStyle}>
        <View style={styles.waveform}>
          {peaks.map((h, i) => {
            const barFraction = (i + 0.5) / WAVEFORM_BARS;
            const played = barFraction <= playedFraction;

            // Fractional distance from the playhead (in "bar units").
            // The pulse falls off smoothly with distance, so as the playhead
            // moves the active "zone" glides instead of jumping bar-to-bar.
            const headFloat = playedFraction * WAVEFORM_BARS;
            const distance = Math.abs(i + 0.5 - headFloat);
            // 0 → full pulse strength, 2 → none. Linear falloff.
            const closeness = isPlaying ? Math.max(0, 1 - distance / 2) : 0;

            const scaleY = pulse.interpolate({
              inputRange: [0, 1],
              // Resting state: 1 (no scale). Peak: 1 + (0.4 * closeness).
              outputRange: [1, 1 + 0.4 * closeness],
            });

            return (
              <Animated.View
                key={i}
                style={[
                  styles.bar,
                  {
                    height: `${Math.max(8, h * 100)}%`,
                    backgroundColor: theme.brand2,
                    opacity: played ? 1 : 0.35,
                    transform: [{ scaleY }],
                  },
                ]}
              />
            );
          })}
        </View>
      </div>

      <NtText style={styles.time}>
        {formatTime(progress)} / {formatTime(duration)}
      </NtText>

      <audio ref={audioRef} preload="none" />
    </View>
  );
}

const waveDivStyle = {
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  height: 30,
};

const makeStyles = (t) =>
  StyleSheet.create({
    container: {
      flexDirection: "row",
      alignItems: "center",
      gap: 8,
      paddingHorizontal: 10,
      paddingVertical: 6,
      backgroundColor: t.surface2,
      borderWidth: 1,
      borderColor: t.borderSoft,
      borderRadius: t.radius,
      alignSelf: "flex-start",
    },
    primaryBtn: {
      width: 26,
      height: 26,
      borderRadius: 13,
      backgroundColor: t.brand,
      alignItems: "center",
      justifyContent: "center",
    },
    secondaryBtn: {
      width: 22,
      height: 22,
      borderRadius: t.radius,
      backgroundColor: t.surface3,
      borderWidth: 1,
      borderColor: t.borderSoft,
      alignItems: "center",
      justifyContent: "center",
    },
    btnDisabled: { opacity: 0.4 },
    waveform: {
      flexDirection: "row",
      alignItems: "center",
      gap: 1,
      height: 22,
      width: 110,
    },
    bar: {
      flex: 1,
      borderRadius: 0.5,
      minHeight: 2,
    },
    time: {
      fontSize: 10,
      color: t.textDim,
      fontFamily: "Menlo",
      minWidth: 70,
      textAlign: "right",
    },
  });

export default NtAudioPlayer;
